import React, { useEffect, useRef, useState } from "react";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckIcon from "@material-ui/icons/Check";
import CircularProgress from "@material-ui/core/CircularProgress";

const QUESTIONS = [
  {
    field: "name",
    title: "Como sua IA deve se chamar?",
    description: "Escolha um nome curto e humano. Exemplo: Cora, Maya ou Ana.",
    placeholder: "Digite o nome da sua IA",
    required: true
  },
  {
    field: "product",
    title: "O que sua empresa vende?",
    description: "Pode ser um produto, serviço ou solução.",
    placeholder: "Exemplo: tratamentos estéticos e procedimentos faciais",
    multiline: true,
    required: true
  },
  {
    field: "audience",
    title: "Quem normalmente compra de vocês?",
    description: "Descreva o principal perfil de cliente.",
    placeholder: "Exemplo: mulheres de 25 a 50 anos que buscam melhorar a autoestima",
    multiline: true,
    required: true
  },
  {
    field: "tone",
    title: "Como sua IA deve conversar?",
    description: "Escolha o estilo que mais combina com sua empresa.",
    type: "options",
    required: true,
    options: [
      {
        value: "vendedor humanizado",
        label: "Humanizado",
        description: "Natural, próximo e comercial"
      },
      {
        value: "consultivo e profissional",
        label: "Consultivo",
        description: "Entende antes de oferecer"
      },
      {
        value: "elegante e acolhedor",
        label: "Elegante",
        description: "Sofisticado e atencioso"
      },
      {
        value: "direto e objetivo",
        label: "Objetivo",
        description: "Rápido, claro e prático"
      }
    ]
  },
  {
    field: "companyInfo",
    title: "O que a IA precisa saber sobre sua empresa?",
    description: "Conte diferenciais, localização, horários ou informações importantes.",
    placeholder: "Exemplo: atendemos em Guarulhos, de segunda a sábado...",
    multiline: true
  },
  {
    field: "productDetails",
    title: "Existe algo importante sobre seus produtos ou serviços?",
    description: "Inclua preços, prazos, garantias, formas de pagamento ou regras.",
    placeholder: "Exemplo: aceitamos Pix e cartão, oferecemos garantia de 6 meses...",
    multiline: true
  }
];

const KoraxOnboardingInterview = ({
  config,
  updateConfig,
  handleSave,
  saving,
  onCancel
}) => {
  const [started, setStarted] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [processing, setProcessing] = useState(false);
  const inputRef = useRef(null);

  const question = QUESTIONS[questionIndex];
  const value = config[question?.field] || "";
  const progress = Math.round(((questionIndex + 1) / QUESTIONS.length) * 100);

  useEffect(() => {
    if (started && !processing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [started, questionIndex, processing]);

  const answerIsValid = () => {
    if (!question.required) return true;
    return String(value).trim().length >= 2;
  };

  const goNext = async () => {
    if (!answerIsValid()) return;

    if (questionIndex < QUESTIONS.length - 1) {
      setQuestionIndex(index => index + 1);
      return;
    }

    setProcessing(true);

    const success = await handleSave();

    if (success === false) {
      setProcessing(false);
    }
  };

  const goBack = () => {
    if (questionIndex > 0) {
      setQuestionIndex(index => index - 1);
    }
  };

  const handleKeyDown = event => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      question.type !== "options"
    ) {
      event.preventDefault();
      goNext();
    }
  };

  const pageStyle = {
    minHeight: "100%",
    width: "100%",
    boxSizing: "border-box",
    background:
      "radial-gradient(circle at top left, rgba(0,102,255,.15), transparent 32%), #F4F7FC",
    display: "flex",
    flexDirection: "column",
    fontFamily: "'Inter', system-ui, sans-serif",
    color: "#0F172A"
  };

  const headerStyle = {
    height: 68,
    padding: "0 28px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "rgba(255,255,255,.88)",
    borderBottom: "1px solid #E2E8F0",
    backdropFilter: "blur(16px)",
    boxSizing: "border-box"
  };

  const logoStyle = {
    width: 38,
    height: 38,
    borderRadius: 12,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 19,
    boxShadow: "0 10px 25px rgba(0,102,255,.22)"
  };

  const buttonStyle = {
    border: "none",
    borderRadius: 14,
    padding: "13px 20px",
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#FFFFFF",
    fontWeight: 900,
    fontSize: 14,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    boxShadow: "0 12px 30px rgba(0,102,255,.22)"
  };

  if (!started) {
    return (
      <div style={pageStyle}>
        <div style={headerStyle}>
          <div style={logoStyle}>K</div>

          <div>
            <div style={{ fontSize: 14, fontWeight: 950 }}>Korax</div>
            <div style={{ fontSize: 11, color: "#64748B" }}>
              Configuração inteligente
            </div>
          </div>
        </div>

        <main
          style={{
            flex: 1,
            padding: "36px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 660,
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: 28,
              padding: "42px 38px",
              boxShadow: "0 28px 80px rgba(15,23,42,.10)",
              boxSizing: "border-box",
              textAlign: "center"
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 24,
                margin: "0 auto 24px",
                background: "linear-gradient(135deg,#0066FF,#00A3FF)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 32,
                boxShadow: "0 18px 42px rgba(0,102,255,.24)"
              }}
            >
              🧠
            </div>

            <div
              style={{
                color: "#0066FF",
                fontWeight: 900,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 1
              }}
            >
              Treinamento inicial
            </div>

            <h1
              style={{
                margin: "10px 0 12px",
                fontSize: "clamp(27px, 5vw, 40px)",
                lineHeight: 1.1,
                letterSpacing: "-1.2px"
              }}
            >
              Vamos criar sua IA juntos.
            </h1>

            <p
              style={{
                margin: "0 auto",
                maxWidth: 500,
                color: "#64748B",
                fontSize: 15,
                lineHeight: 1.7
              }}
            >
              Responda algumas perguntas rápidas. A Korax organizará as
              informações e preparará seu primeiro agente automaticamente.
            </p>

            <button
              style={{ ...buttonStyle, marginTop: 28 }}
              onClick={() => setStarted(true)}
            >
              Começar treinamento
              <ArrowForwardIcon style={{ fontSize: 18 }} />
            </button>

            <button
              onClick={onCancel}
              style={{
                display: "block",
                margin: "18px auto 0",
                border: "none",
                background: "transparent",
                color: "#64748B",
                fontSize: 12,
                fontWeight: 800,
                cursor: "pointer"
              }}
            >
              Voltar ao onboarding
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (processing) {
    const processingItems = [
      "Entendendo sua empresa",
      "Construindo a personalidade",
      "Organizando o atendimento",
      "Preparando as respostas",
      "Finalizando seu agente"
    ];

    return (
      <div style={pageStyle}>
        <main
          style={{
            flex: 1,
            padding: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 560,
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: 28,
              padding: "42px 38px",
              boxShadow: "0 28px 80px rgba(15,23,42,.10)",
              boxSizing: "border-box"
            }}
          >
            <div style={{ textAlign: "center" }}>
              <CircularProgress size={52} thickness={4} />

              <h2 style={{ margin: "24px 0 8px", fontSize: 25 }}>
                Criando sua IA...
              </h2>

              <p style={{ margin: 0, color: "#64748B", lineHeight: 1.6 }}>
                Estamos transformando suas respostas em uma estrutura de
                atendimento pronta para começar.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                marginTop: 28
              }}
            >
              {processingItems.map((item, index) => (
                <div
                  key={item}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    padding: "12px 14px",
                    borderRadius: 14,
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    color: index === 4 ? "#0066FF" : "#334155",
                    fontSize: 13,
                    fontWeight: 800
                  }}
                >
                  {index === 4 && saving ? (
                    <CircularProgress size={17} />
                  ) : (
                    <CheckIcon
                      style={{ fontSize: 18, color: "#22C55E" }}
                    />
                  )}

                  {item}
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div style={logoStyle}>K</div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 950 }}>
            Treinamento da sua IA
          </div>

          <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
            Pergunta {questionIndex + 1} de {QUESTIONS.length}
          </div>
        </div>

        <div
          style={{
            fontSize: 12,
            color: "#0066FF",
            fontWeight: 900
          }}
        >
          {progress}%
        </div>
      </div>

      <div
        style={{
          height: 4,
          background: "#E2E8F0",
          flexShrink: 0
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            background: "linear-gradient(90deg,#0066FF,#00A3FF)",
            transition: "width .35s ease"
          }}
        />
      </div>

      <main
        style={{
          flex: 1,
          padding: "38px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <div style={{ width: "100%", maxWidth: 680 }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 14,
              marginBottom: 18
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 14,
                flexShrink: 0,
                background: "linear-gradient(135deg,#0066FF,#00A3FF)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
                boxShadow: "0 10px 25px rgba(0,102,255,.20)"
              }}
            >
              ✨
            </div>

            <div
              style={{
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                borderRadius: "5px 22px 22px 22px",
                padding: "20px 22px",
                boxShadow: "0 16px 40px rgba(15,23,42,.07)",
                width: "100%",
                boxSizing: "border-box"
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "clamp(21px, 4vw, 29px)",
                  letterSpacing: "-.6px",
                  lineHeight: 1.25
                }}
              >
                {question.title}
              </h2>

              <p
                style={{
                  margin: "8px 0 0",
                  color: "#64748B",
                  lineHeight: 1.55,
                  fontSize: 13
                }}
              >
                {question.description}
              </p>
            </div>
          </div>

          <div
            style={{
              marginLeft: 56,
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: 22,
              padding: 18,
              boxShadow: "0 16px 40px rgba(15,23,42,.06)"
            }}
          >
            {question.type === "options" ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))",
                  gap: 10
                }}
              >
                {question.options.map(option => {
                  const active = value === option.value;

                  return (
                    <button
                      key={option.value}
                      onClick={() =>
                        updateConfig(question.field, option.value)
                      }
                      style={{
                        textAlign: "left",
                        borderRadius: 16,
                        padding: "15px 16px",
                        border: active
                          ? "2px solid #0066FF"
                          : "1px solid #E2E8F0",
                        background: active
                          ? "rgba(0,102,255,.06)"
                          : "#FFFFFF",
                        cursor: "pointer",
                        color: "#0F172A"
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 950,
                          fontSize: 14,
                          color: active ? "#0066FF" : "#0F172A"
                        }}
                      >
                        {active ? "✓ " : ""}
                        {option.label}
                      </div>

                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 11,
                          color: "#64748B",
                          lineHeight: 1.5
                        }}
                      >
                        {option.description}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : question.multiline ? (
              <textarea
                ref={inputRef}
                value={value}
                onChange={event =>
                  updateConfig(question.field, event.target.value)
                }
                onKeyDown={handleKeyDown}
                placeholder={question.placeholder}
                rows={5}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid #CBD5E1",
                  borderRadius: 16,
                  padding: "15px 16px",
                  resize: "vertical",
                  minHeight: 125,
                  outline: "none",
                  color: "#0F172A",
                  fontFamily: "inherit",
                  fontSize: 14,
                  lineHeight: 1.6
                }}
              />
            ) : (
              <input
                ref={inputRef}
                value={value}
                onChange={event =>
                  updateConfig(question.field, event.target.value)
                }
                onKeyDown={handleKeyDown}
                placeholder={question.placeholder}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid #CBD5E1",
                  borderRadius: 16,
                  padding: "15px 16px",
                  outline: "none",
                  color: "#0F172A",
                  fontFamily: "inherit",
                  fontSize: 14
                }}
              />
            )}

            <div
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12
              }}
            >
              <button
                onClick={goBack}
                disabled={questionIndex === 0}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#64748B",
                  fontSize: 13,
                  fontWeight: 850,
                  cursor: questionIndex === 0 ? "default" : "pointer",
                  opacity: questionIndex === 0 ? 0 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 5
                }}
              >
                <ArrowBackIcon style={{ fontSize: 17 }} />
                Voltar
              </button>

              <button
                onClick={goNext}
                disabled={!answerIsValid() || saving}
                style={{
                  ...buttonStyle,
                  opacity: answerIsValid() && !saving ? 1 : 0.45,
                  cursor:
                    answerIsValid() && !saving
                      ? "pointer"
                      : "not-allowed"
                }}
              >
                {questionIndex === QUESTIONS.length - 1
                  ? "Criar minha IA"
                  : "Continuar"}

                <ArrowForwardIcon style={{ fontSize: 18 }} />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default KoraxOnboardingInterview;
